/**
 * ThemeProvider — hält das Farbschema (light | dark | system) und spiegelt
 * den aufgelösten Wert als Klasse aufs <html>-Element:
 *
 *   <html>            — Light
 *   <html class="dark">  — Dark
 *
 * Die Tokens stehen in `src/index.css` (`:root` / `.dark`); Tailwind-Klassen
 * wie `bg-background`, `text-foreground`, `shadow-card`, `rounded-lg` lösen
 * sie über CSS-Variablen auf.
 *
 * **Bereich** (patient/klinik/ambulant/admin) und **Modus** (schlicht/voll)
 * sind eigene, orthogonale Achsen — siehe `AreaContext` bzw. `ModeContext`.
 * Sie spiegeln ihre eigenen Klassen (`.area-*`, `.mode-*`) aufs <html> und
 * sind bewusst von der Theme-Wahl getrennt.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

type Ctx = {
  /** Rohwahl des Nutzers — kann 'system' sein. */
  theme: ThemeChoice;
  /** Tatsächlich aktives Theme nach System-Auflösung. */
  resolved: ResolvedTheme;
  setTheme: (t: ThemeChoice) => void;
};

const STORAGE_KEY = 'nutriflow-theme';

const ThemeContext = createContext<Ctx | null>(null);

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function applyClasses(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Statusbar-Tint für iOS Safari.
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>(() => readStored());
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Icarus D&D Redesign: das Designsystem ist als reines Light-Design
  // spezifiziert (design_handoff_icarus_dnd/README.md — alle Token nur
  // light). Bis ein Dark-Theme gestaltet ist, wird resolved hart auf
  // 'light' geklemmt; die gespeicherte Nutzer-Präferenz bleibt erhalten
  // und greift wieder, sobald die Klemme entfernt wird.
  void systemDark;
  const resolved: ResolvedTheme = 'light';

  useEffect(() => {
    applyClasses(resolved);
  }, [resolved]);

  const setTheme = (t: ThemeChoice) => {
    setThemeState(t);
    window.localStorage.setItem(STORAGE_KEY, t);
  };

  const value = useMemo<Ctx>(() => ({ theme, resolved, setTheme }), [theme, resolved]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
